import { RecursiveStringifyEnums } from '@/testData/buildDistro';
import {
    AppPlatform,
    CompactFeaturesStepData,
    FeatureItem,
    Release,
    ReleaseStepFeatureReadinessData,
    ResolvedFeatureAffiliation,
} from '@/types';
import { massageFeatureItem } from '@/helpers/demoHelpers';

export const getCompactDataFromFeaturesStep = (
    featuresStep: RecursiveStringifyEnums<ReleaseStepFeatureReadinessData>,
): CompactFeaturesStepData => {
    const completedItemsCount = featuresStep.items!.filter((item) => item.isItemDone).length;
    const pendingItemsCount = featuresStep.items!.length - completedItemsCount;
    const totalItemsCount = featuresStep.items!.length;
    const percentFeatureComplete = (completedItemsCount / totalItemsCount) * 100;

    return {
        completedItemsCount,
        pendingItemsCount,
        totalItemsCount,
        percentFeatureComplete,
    };
};

export const featuresStep: (platform: AppPlatform) => RecursiveStringifyEnums<ReleaseStepFeatureReadinessData> = (
    platform,
) => {
    const featuresStep: RecursiveStringifyEnums<ReleaseStepFeatureReadinessData> = {
        appStoreSelectedBuilds: [],
        doesBranchExist: false,
        latestStagingBuild:
            platform === AppPlatform.Android ?
                {
                    ciBuild: {
                        buildHash: '5dGgafjgm3ECgAvSmUmyok8bFZY=',
                        buildIdentifier: '275',
                        integrationId: 'bitrise',
                        buildUrl: 'https://app.bitrise.io',
                        buildStartedAt: '2022-03-15T18:06:21Z',
                        buildFinishedAt: '2022-03-15T18:09:36Z',
                        buildStatus: 'success',
                        providerBuildStatusString: 'Success',
                        commit: '8377f75e11ef0544ac5d7b138f8ec1af913203be',
                        commitMessage:
                            'Bumping version to 1.25.0 (#41)\n\nCo-authored-by: runwayhelperdev[bot] \u003c68658124+runwayhelperdev[bot]@users.noreply.github.com\u003e',
                        commitAuthor: 'runwayhelperdev[bot]',
                        artifacts: [],
                        commitUrl: '',
                    },
                    associatedAppStoreBuild: {
                        buildNumber: '27500',
                        id: '27500',
                        integrationId: 'google-beta',
                        isProcessed: true,
                        type: 'android-aab',
                        uploadedAt: '2022-03-15T18:11:14.055381Z',
                    },
                    isSelectedBuild: true,
                    selectedReason: 'autoRunwaySelection',
                }
            : platform === AppPlatform.Ios ?
                {
                    ciBuild: {
                        commitUrl: '',
                        buildHash: '123',
                        buildIdentifier: '298',
                        integrationId: 'bitrise',
                        buildUrl: 'https://app.bitrise.io/build/123',
                        buildStartedAt: '2022-03-21T17:23:53Z',
                        buildFinishedAt: '2022-03-21T17:32:25Z',
                        buildStatus: 'success',
                        providerBuildStatusString: 'Success',
                        commit: '0fb22bf26bc7c458a518c08d46c162d3045cf329',
                        commitMessage:
                            'Merge pull request #119 from demo/development\n\nRunway promoted branch `development` to `main`.',
                        commitAuthor: 'runway-github[bot]',
                        artifacts: [],
                    },
                    associatedAppStoreBuild: null,
                    isSelectedBuild: true,
                    selectedReason: 'autoRunwaySelection',
                }
            :   {
                    ciBuild: {
                        buildHash: 'fIYLHm7c9r5yxKacHK9H41G7eG8=',
                        buildIdentifier: '15',
                        integrationId: 'github-ci',
                        buildUrl: 'https://github.com/demo/fake-react-native/actions/runs/12345678',
                        buildStartedAt: '2022-02-22T15:17:12Z',
                        buildFinishedAt: '2022-02-22T15:19:47Z',
                        buildStatus: 'success',
                        providerBuildStatusString: 'Success',
                        commit: '484d3fe5fc49db90faf4a97508c009480e85c397',
                        commitMessage:
                            'Merge pull request #4 from demo/runway-bump-1.3.0-1645047156\n\nBumping version to 1.3.0',
                        commitAuthor: 'Amelia Earhart',
                        artifacts: [],
                        commitUrl: '',
                    },
                    associatedAppStoreBuild: null,
                    isSelectedBuild: true,
                    selectedReason: 'autoRunwaySelection',
                },
        latestWorkingBranchBuild:
            platform === AppPlatform.Ios ?
                {
                    ciBuild: {
                        commitUrl: '',
                        buildHash: 'dMJkmvM1W8xPEMXGCjTpC1erm6g=',
                        buildIdentifier: '294',
                        integrationId: 'bitrise',
                        buildUrl: 'https://app.bitrise.io/build/123',
                        buildStartedAt: '2022-03-15T15:40:40Z',
                        buildFinishedAt: '2022-03-15T15:49:56Z',
                        buildStatus: 'success',
                        providerBuildStatusString: 'Success',
                        commit: '6b8b4b602bdcfd03ab27c3b65a348267b82321da',
                        commitMessage: 'fix logic',
                        commitAuthor: 'gAldrin',
                        artifacts: null,
                    },
                    associatedAppStoreBuild: null,
                }
            :   null,
        releaseBranch: 'release-1.0.0',
        selectedStagingBuild: undefined,
        customTicketFields: [],
        workingBranch: '',
        items: [
            {
                id: 'c7b9893ec8cdac8fb4882087fba092ee',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '8300d65',
                    url: 'https://github.com/demo/DEMO-ios/commit/8300d65cbede86304ef9ba909ecb8e3e64bfa1cb',
                    author: 'runway-github[bot]',
                    message: 'Bumping version to 3.97.0 (#117)\n\nRunway bumped your version to 3.97.0',
                    branch: 'main',
                    committedAt: '2022-03-03T17:55:09Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: '7224f5c516e346b61be50b0428644f10',
                ticket: {
                    identifier: 'DEMO-25',
                    description: 'Disable unused failing tests',
                    fullDescription:
                        "Some tests aren't used anymore and are failing. We should just disable those for now.",
                    url: 'https://demo.atlassian.net/browse/DEMO-22',
                    owner: 'Sully Sullenberger',
                    reporter: 'Sully Sullenberger',
                    status: 'Done',
                    createdAt: '2022-02-09T10:10:43.828-06:00',
                    updatedAt: '2022-03-18T17:49:37.403-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '36d02a3',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'ssulleng',
                    message: 'DEMO-25 Fix test build, disable failing tests',
                    branch: 'main',
                    committedAt: '2022-03-10T02:24:44Z',
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedWithTicket',
                fixRequest: {
                    id: '7224f5c516e346b61be50b0428644f10',
                    createdBy: 'aearhart@demo.com',
                    createdAt: '2022-04-18T17:53:35.403-06:00',
                    details: 'We need to pull in this changes to the release',
                    status: {
                        status: 'Approved',
                        updatedAt: '2022-04-18T17:53:35.403-06:00',
                        authorId: 'narmstrong@demo.com',
                    },
                },
            },
            {
                id: '601c6f6b2a018401622f79f7d8fb785f',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '0fb22bf',
                    url: 'https://github.com/demo/DEMO-ios/commit/0fb22bf26bc7c458a518c08d46c162d3045cf329',
                    author: 'runway-github[bot]',
                    message: 'Merge pull request #119 from demo/development\n\nRunway promoted branch.',
                    branch: 'main',
                    committedAt: '2022-03-11T19:55:15Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: '556eb16726641438292ca2c5a97f6e98',
                ticket: {
                    identifier: 'DEMO-7',
                    description: 'New thing for latest release',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-7',
                    owner: 'Sully Sullenberger',
                    reporter: 'Amelia Earhart',
                    status: 'In Progress',
                    createdAt: '2021-07-27T11:41:15.179-05:00',
                    updatedAt: '2022-03-22T07:30:59.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        { resolvedString: 'design-polish', integrationId: 'jira', affiliationType: 'jira-label' },
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: 'ba907ac',
                    url: 'https://github.com/demo/DEMO-ios/commit/ba907acdd335ac0ba1006b46b0ec52cbacad4d58',
                    author: 'Sully Sullenberger',
                    message: 'DEMO-7 new thing (#120)',
                    branch: 'main',
                    committedAt: '2022-03-14T22:31:19Z',
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeUnmergedWithTicket',
                fixRequest: {
                    id: '556eb16726641438292ca2c5a97f6e98',
                    createdBy: 'narmstrong@demo.com',
                    createdAt: '2022-03-18T17:53:35.403-06:00',
                    details: 'Can we pull in this change to the release?',
                    status: {
                        status: 'Approved',
                        updatedAt: '2022-03-19T20:53:35.403-06:00',
                        authorId: 'aearhart@demo.com',
                    },
                },
            },
            {
                id: '556eb167266414382',
                ticket: {
                    identifier: 'DEMO-23',
                    description: 'User cannot favorite items from homepage',
                    fullDescription:
                        'When attempting to favorite an item from the list of highlighted items on the homepage carousel, the app crashes.',
                    url: 'https://demo.atlassian.net/browse/DEMO-23',
                    owner: 'Sully Sullenberger',
                    reporter: 'Amelia Earhart',
                    status: 'QA',
                    createdAt: '2021-07-28T1:41:15.179-05:00',
                    updatedAt: '2022-08-8T07:30:59.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'bug',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'openPR',
                    type: 'pullRequest',
                    identifier: '32',
                    url: 'https://github.com/demo/DEMO-ios/pull/32',
                    author: 'Neil Armstrong',
                    title: 'DEMO-23 Fix marking item as favorite from homepage',
                    baseBranch: 'release-1.1.0',
                    headBranch: 'fix-favoriting',
                    pullRequestStatus: 'open',
                    updatedAt: '2022-03-25T22:31:19Z',
                    featureAffiliations: [],
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'codeUnmergedWithTicket',
                fixRequest: {
                    id: '556eb16726641438292ca2c5a97f6e98',
                    createdBy: 'narmstrong@demo.com',
                    createdAt: '2022-03-18T17:53:35.403-06:00',
                    details:
                        'This is a critical fix for users favoriting items from the main homepage. Impact is ~2% of users per week.',
                    status: null,
                },
            },
            {
                id: '886214db270bb25e198281bf2d723c72',
                ticket: {
                    identifier: 'DEMO-22',
                    description: 'Add CTA in profile page for deleting account',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-22',
                    owner: 'Sully Sullenberger',
                    reporter: 'Sully Sullenberger',
                    status: 'In Progress',
                    createdAt: '2022-02-09T10:10:43.828-06:00',
                    updatedAt: '2022-03-18T17:49:37.403-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: true,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    pullRequestStatus: 'open',
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    identifier: '109',
                    type: 'pullRequest',
                    status: 'unmerged',
                    title: 'Delete account CTA!',
                    description: '- do not merge',
                    baseBranch: 'development',
                    headBranch: 'DEMO-22-delete-cta',
                    url: 'https://github.com/demo/DEMO-ios/pull/109',
                    author: 'ssulleng',
                    createdAt: '2022-02-14T21:54:12Z',
                    updatedAt: '2022-03-01T20:25:42Z',
                    changesVolume: {
                        added: 1,
                        deleted: 0,
                        changed: 1,
                        changedFiles: 1,
                        commitsCount: 1,
                        codeDiffUrl: 'https://github.com/demo/DEMO-ios/pull/109/files',
                    },
                    featureAffiliations: [],
                },
                associatedStabilityIssues: [],
                explanationText:
                    "This code doesn't exist in the diff for the release, but it references (in its commit message, PR title, or branch name) a ticket which is associated with the release via a feature affiliation",
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'codeUnmergedWithTicket',
                fixRequest: {
                    id: '886214db270bb25e198281bf2d723c72',
                    createdBy: 'narmstrong@demo.com',
                    createdAt: '2022-03-03T17:53:35.403-06:00',
                    details: 'We need to include this change on the next release.',
                    status: {
                        status: 'Rejected',
                        updatedAt: '2022-03-04T20:53:35.403-06:00',
                        authorId: 'aearhart@demo.com',
                    },
                },
            },
            {
                id: 'f7c0168b2b09e5b123bf2af07ff90841',
                ticket: {
                    identifier: 'DEMO-15',
                    description: 'Formatting and cleanup',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-15',
                    owner: 'Amelia Earhart',
                    reporter: 'Amelia Earhart',
                    status: 'In Progress',
                    createdAt: '2021-04-22T09:58:35.143-05:00',
                    updatedAt: '2022-03-18T17:41:08.618-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    pullRequestStatus: 'open',
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    identifier: '93',
                    type: 'pullRequest',
                    status: 'unmerged',
                    title: '[DEMO-15] Making some changes to go in a PR, editing title',
                    description: null,
                    baseBranch: 'development',
                    headBranch: 'amelia-pr-branch-1',
                    url: 'https://github.com/demo/DEMO-ios/pull/93',
                    author: 'aearhart',
                    createdAt: '2021-10-27T18:48:50Z',
                    updatedAt: '2022-03-01T20:25:16Z',
                    changesVolume: {
                        added: 2,
                        deleted: 1,
                        changed: 3,
                        changedFiles: 1,
                        commitsCount: 1,
                        codeDiffUrl: 'https://github.com/demo/DEMO-ios/pull/93/files',
                    },
                    featureAffiliations: [],
                },
                associatedStabilityIssues: [],
                explanationText:
                    "This code doesn't exist in the diff for the release, but it references (in its commit message, PR title, or branch name) a ticket which is associated with the release via a feature affiliation",
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'codeUnmergedWithTicket',
            },
            {
                id: '83541443f62cee376714c9bca54b1172',
                ticket: {
                    identifier: 'DEMO-10',
                    description: 'Migrate old user models',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-10',
                    owner: 'Amelia Earhart',
                    reporter: 'Amelia Earhart',
                    status: 'In Progress',
                    createdAt: '2021-01-12T14:56:47.903-06:00',
                    updatedAt: '2022-03-22T07:30:26.552-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: null,
                associatedStabilityIssues: [],
                explanationText:
                    'This ticket is associated with the release via a feature affiliation; no code in the release references this ticket',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'noCodeOnlyTicket',
            },
            {
                id: 'f6b2d4a2e8ab469fa2d57865bbad172d',
                ticket: {
                    identifier: 'DEMO-3451',
                    description: 'Add dark mode support',
                    fullDescription:
                        'Implement dark mode support across the entire app UI. Ensure all screens and components adapt to dark mode seamlessly. Design references: https://www.figma.com/file/XYZ/Dark-Mode-Design',
                    url: 'https://demo.atlassian.net/browse/DEMO-3451',
                    owner: 'Alice Johnson',
                    reporter: 'Bob Smith',
                    status: 'In Progress',
                    createdAt: '2024-05-01T10:15:30.000Z',
                    updatedAt: '2024-06-15T11:20:00.000Z',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'd89f123',
                    url: 'https://github.com/demo/DEMO-ios/commit/8300d65cbede86304ef9ba909ecb8e3e64bfa1cb',
                    author: 'alicejohnson',
                    message:
                        '[DEMO-3451] Add dark mode support (#1234)\n\n* Add dark mode toggle\n* Update UI components for dark mode\n* Fix styling issues in dark mode',
                    branch: 'release/1.2.0',
                    committedAt: '2024-06-14T14:00:00Z',
                    sourcePR: {
                        number: 1234,
                        url: 'https://github.com/demo/DEMO-ios/pull/1234',
                        featureAffiliations: [
                            {
                                resolvedString: '3.0.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: [],
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
            {
                id: 'ab5c3d4e6f8d4f07a9d765dcb9b123d4',
                ticket: {
                    identifier: 'DEMO-1237',
                    description: 'Optimize app startup time',
                    fullDescription:
                        'Reduce the startup time of the mobile app by optimizing resource loading and initialization processes. Analyze and address performance bottlenecks.',
                    url: 'https://demo.atlassian.net/browse/DEMO-1237',
                    owner: 'Emma Davis',
                    reporter: 'Dana White',
                    status: 'Done',
                    createdAt: '2024-04-15T08:00:00.000Z',
                    updatedAt: '2024-06-10T12:30:00.000Z',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'performance',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'e34f567',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'emmadavis',
                    message:
                        '[DEMO-1237] Optimize startup time (#5678)\n\n* Improve resource loading\n* Reduce initialization overhead\n* Remove unnecessary delays',
                    branch: 'release/3.0.0',
                    committedAt: '2024-06-09T10:00:00Z',
                    sourcePR: {
                        number: 5678,
                        url: 'https://github.com/demo/DEMO-ios/pull/5678',
                        featureAffiliations: [
                            {
                                resolvedString: 'performance',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                            {
                                resolvedString: '3.0.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: [],
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
            {
                id: 'd8764b8e4766195c12e7c3e418ab9432',
                ticket: {
                    identifier: 'DEMO-1924',
                    description: 'Enhance login security',
                    fullDescription: 'Implement two-factor authentication for enhanced security during user login.',
                    url: 'https://jira.example.com/browse/DEMO-1924',
                    owner: 'Maria Gonzalez',
                    reporter: 'Maria Gonzalez',
                    status: 'In Review',
                    createdAt: '2024-04-30T10:15:00.000+02:00',
                    updatedAt: '2024-06-14T09:56:21.877+02:00',
                    isTicketMissingFeatureAffiliation: true,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'security',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'fdbef35',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'mariagonzalez',
                    message:
                        '[DEMO-1924] Implement two-factor authentication (#2827)\n\n\r\nCo-authored-by: Igor Fedotov',
                    branch: 'develop',
                    committedAt: '2024-05-01T14:22:13Z',
                    sourcePR: {
                        number: 2827,
                        url: 'https://github.com/demo/DEMO-ios/pull/3248',
                        featureAffiliations: [
                            {
                                resolvedString: 'Needed for Release 👀',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                            {
                                resolvedString: '2.74.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: null,
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff on the working branch since last release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: null,
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
            {
                id: '781eb167266414383',
                ticket: {
                    identifier: 'DEMO-45',
                    description: 'Search functionality returns no results',
                    fullDescription:
                        'When using the search bar to find items, no results are displayed even though items exist.',
                    url: 'https://demo.atlassian.net/browse/DEMO-45',
                    owner: 'Katherine Johnson',
                    reporter: 'Yuri Gagarin',
                    status: 'In Progress',
                    createdAt: '2021-08-15T09:15:45.179-05:00',
                    updatedAt: '2022-09-10T11:25:37.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'bug',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'openPR',
                    type: 'pullRequest',
                    identifier: '47',
                    url: 'https://github.com/demo/DEMO-ios/pull/47',
                    author: 'Alan Shepard',
                    title: 'DEMO-45 Fix search results issue',
                    baseBranch: 'release-1.2.0',
                    headBranch: 'fix-search',
                    pullRequestStatus: 'open',
                    updatedAt: '2022-04-15T13:41:29Z',
                    featureAffiliations: [],
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'codeUnmergedWithTicket',
            },
            {
                id: 'bc5d3e7f9d8f5f08a9e765dcb9b234e5',
                ticket: {
                    identifier: 'DEMO-2348',
                    description: 'Improve app navigation speed',
                    fullDescription:
                        'Enhance the navigation speed of the mobile app by optimizing the routing and state management. Identify and resolve any performance issues.',
                    url: 'https://demo.atlassian.net/browse/DEMO-2348',
                    owner: 'Carlos Martinez',
                    reporter: 'Dana White',
                    status: 'Done',
                    createdAt: '2024-05-20T08:00:00.000Z',
                    updatedAt: '2024-07-10T12:30:00.000Z',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'performance',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'f56g789',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'carlosmartinez',
                    message:
                        '[DEMO-2348] Improve navigation speed (#6789)\n\n* Optimize routing\n* Enhance state management\n* Fix performance issues',
                    branch: 'release/4.0.0',
                    committedAt: '2024-07-09T10:00:00Z',
                    sourcePR: {
                        number: 6789,
                        url: 'https://github.com/demo/DEMO-ios/pull/6789',
                        featureAffiliations: [
                            {
                                resolvedString: 'performance',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                            {
                                resolvedString: '4.0.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: [],
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
        ],
        workingBranchItems: [
            {
                id: 'c7b9893ec8cdac8fb4882087fba092ee',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '8300d65',
                    url: 'https://github.com/demo/DEMO-ios/commit/8300d65cbede86304ef9ba909ecb8e3e64bfa1cb',
                    author: 'runway-github[bot]',
                    message: 'Bumping version (#117)\n\nRunway bumped your version.',
                    branch: 'development',
                    committedAt: '2022-03-03T17:55:09Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: '7224f5c516e346b61be50b0428644f10',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '36d02a3',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'ssulleng',
                    message: 'Fix test build, disable failing tests',
                    branch: 'development',
                    committedAt: '2022-03-10T02:24:44Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: '556eb16726641438292ca2c5a97f6e98',
                ticket: {
                    identifier: 'DEMO-7',
                    description: 'New thing for latest release',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-7',
                    owner: 'Sully Sullenberger',
                    reporter: 'Amelia Earhart',
                    status: 'In Progress',
                    createdAt: '2021-07-27T11:41:15.179-05:00',
                    updatedAt: '2022-03-22T07:30:59.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        { resolvedString: 'design-polish', integrationId: 'jira', affiliationType: 'jira-label' },
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: 'ba907ac',
                    url: 'https://github.com/demo/DEMO-ios/commit/ba907acdd335ac0ba1006b46b0ec52cbacad4d58',
                    author: 'ssulleng',
                    message: 'DEMO-7 new thing (#120)',
                    branch: 'development',
                    committedAt: '2022-03-14T22:31:18Z',
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedWithTicket',
                fixRequest: {
                    id: '556eb16726641438292ca2c5a97f6e98',
                    createdBy: 'narmstrong@demo.com',
                    createdAt: '2022-03-18T17:53:35.403-06:00',
                    details: 'Can we pull in this change to the release?',
                    status: {
                        status: 'Approved',
                        updatedAt: '2022-03-19T20:53:35.403-06:00',
                        authorId: 'aearhart@demo.com',
                    },
                },
            },
            {
                id: 'e8fad27eca7280e42f48829e5063e1d5',
                ticket: {
                    identifier: 'DEMO-45',
                    description: 'Fix Xcode 13.3 build',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-7',
                    owner: 'Buzz Aldrin',
                    reporter: 'Amelia Earhart',
                    status: 'Done',
                    createdAt: '2021-07-27T11:41:15.179-05:00',
                    updatedAt: '2022-03-22T07:30:59.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: 'aae50f8',
                    url: 'https://github.com/demo/DEMO-ios/commit/aae50f846a3043d7980128412b685204f4c66135',
                    author: 'baldrin',
                    message: 'DEMO-45 Fixed compilation error Xcode 13.3',
                    branch: 'development',
                    committedAt: '2022-03-15T11:23:54Z',
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedWithTicket',
            },
            {
                id: '4b28486ba5409dc4e02dbfd5a98c49fe',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: 'fec1e4c',
                    url: 'https://github.com/demo/DEMO-ios/commit/fec1e4c265b5307cbf6c970dd4d434c55af8cf9e',
                    author: 'baldrin',
                    message: 'Removed a couple of warnings',
                    branch: 'development',
                    committedAt: '2022-03-15T11:25:47Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: '2241b0afcf7b00ca366a537f4e30c53c',
                ticket: {
                    identifier: 'DEMO-44',
                    description: 'Update all necessary pods',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-7',
                    owner: 'Buzz Aldrin',
                    reporter: 'Buzz Aldrin',
                    status: 'Done',
                    createdAt: '2021-07-27T11:41:15.179-05:00',
                    updatedAt: '2022-03-22T07:30:59.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: 'bd4f9bc',
                    url: 'https://github.com/demo/DEMO-ios/commit/bd4f9bc7ec241e53817700b5c64fdf4575f04519',
                    author: 'baldrin',
                    message: 'Pods update',
                    branch: 'development',
                    committedAt: '2022-03-15T11:30:32Z',
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedWithTicket',
                fixRequest: {
                    id: '2241b0afcf7b00ca366a537f4e30c53c',
                    createdBy: 'ssullenberger@demo.com',
                    createdAt: '2022-03-18T17:53:35.403-06:00',
                    details: 'Can we pull in this change to the release?',
                    status: {
                        status: 'Pending',
                        updatedAt: '2022-03-19T20:53:35.403-06:00',
                        authorId: 'mjemison@demo.com',
                    },
                },
            },
            {
                id: '175b9838b7a7e5083f3642ca0d1f666b',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: 'fa37ffa',
                    url: 'https://github.com/demo/DEMO-ios/commit/fa37ffa14b6a639966e187e280d1bf155b70b869',
                    author: 'baldrin',
                    message: 'Pods cleanup',
                    branch: 'development',
                    committedAt: '2022-03-15T11:32:57Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: '6d1fa055638f9f3fccb6d6a8c9707c36',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '5c900ed',
                    url: 'https://github.com/demo/DEMO-ios/commit/5c900ed4810ecc8dce6deaed33b0c767694fa35d',
                    author: 'narmstrong',
                    message: 'Merge pull request #121 from demo/ale-dependencies-update\n\nAle dependencies update',
                    branch: 'development',
                    committedAt: '2022-03-15T15:27:29Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: 'fcc385d38495a9a53855f5f188d03191',
                ticket: {
                    identifier: 'DEMO-43',
                    description: 'Add and install sentry pod for crash monitoring/reporting',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-7',
                    owner: 'Neil Armstrong',
                    reporter: 'Neil Armstrong',
                    status: 'Done',
                    createdAt: '2021-07-27T11:41:15.179-05:00',
                    updatedAt: '2022-03-22T07:30:59.225-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'DEMO',
                        displayableName: 'Demo-Core',
                        url: 'https://demo.atlassian.net/browse/DEMO',
                    },
                    featureAffiliations: [
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '246e89b',
                    url: 'https://github.com/demo/DEMO-ios/commit/246e89b55d1d63a2ae1de5a8b153e0c950dacb87',
                    author: 'narmstrong',
                    message: 'DEMO-43 Configure sentry',
                    branch: 'development',
                    committedAt: '2022-03-15T15:33:45Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
                fixRequest: {
                    id: 'fcc385d38495a9a53855f5f188d03191',
                    createdBy: 'ssullenberger@demo.com',
                    createdAt: '2022-03-18T17:53:35.403-06:00',
                    details: 'We should definitely pull in this item as a fix.',
                    status: {
                        status: 'Approved',
                        updatedAt: '2022-03-19T20:53:35.403-06:00',
                        authorId: 'mjemison@demo.com',
                    },
                },
            },
            {
                id: 'a658053d035a03df8962ad5087e53089',
                ticket: null,
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    status: 'merged',
                    type: 'commit',
                    identifier: '6b8b4b6',
                    url: 'https://github.com/demo/DEMO-ios/commit/6b8b4b602bdcfd03ab27c3b65a348267b82321da',
                    author: 'narmstrong',
                    message: 'fix logic',
                    branch: 'development',
                    committedAt: '2022-03-15T15:40:34Z',
                },
                associatedStabilityIssues: [],
                explanationText: 'This code exists in the diff for the release; no ticket is referenced by this code',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                debugVariant: 'codeMergedNoTicket',
            },
            {
                id: 'f7c0168b2b09e5b123bf2af07ff90841',
                ticket: {
                    identifier: 'DEMO-15',
                    description: 'Formatting and cleanup',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-15',
                    owner: 'Amelia Earhart',
                    reporter: 'Amelia Earhart',
                    status: 'In Progress',
                    createdAt: '2021-04-22T09:58:35.143-05:00',
                    updatedAt: '2022-03-18T17:41:08.618-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    pullRequestStatus: 'open',
                    identifier: '93',
                    type: 'pullRequest',
                    status: 'openPR',
                    title: '[DEMO-15] Making some changes to go in a PR, editing title',
                    description: null,
                    baseBranch: 'development',
                    headBranch: 'dave-pr-branch-1',
                    url: 'https://github.com/demo/DEMO-ios/pull/93',
                    author: 'aearhart',
                    createdAt: '2021-10-27T18:48:50Z',
                    updatedAt: '2022-03-01T20:25:16Z',
                    changesVolume: {
                        added: 2,
                        deleted: 1,
                        changed: 3,
                        changedFiles: 1,
                        commitsCount: 1,
                        codeDiffUrl: 'https://github.com/demo/DEMO-ios/pull/93/files',
                    },
                    featureAffiliations: [],
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This PR is open against the release, and it references this ticket in its title or branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'openPRWithTicket',
            },
            {
                id: '886214db270bb25e198281bf2d723c72',
                ticket: {
                    identifier: 'DEMO-22',
                    description: 'Add CTA in profile page for deleting account',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-22',
                    owner: 'Sully Sullenberger',
                    reporter: 'Sully Sullenberger',
                    status: 'In Progress',
                    createdAt: '2022-02-09T10:10:43.828-06:00',
                    updatedAt: '2022-03-18T17:49:37.403-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: true,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: {
                    sourcePR: null,
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    pullRequestStatus: 'open',
                    identifier: '109',
                    type: 'pullRequest',
                    status: 'openPR',
                    title: 'Delete account CTA!',
                    description: '- do not merge',
                    baseBranch: 'development',
                    headBranch: 'DEMO-22-delete-cta',
                    url: 'https://github.com/demo/DEMO-ios/pull/109',
                    author: 'ssulleng',
                    createdAt: '2022-02-14T21:54:12Z',
                    updatedAt: '2022-03-01T20:25:42Z',
                    changesVolume: {
                        added: 1,
                        deleted: 0,
                        changed: 1,
                        changedFiles: 1,
                        commitsCount: 1,
                        codeDiffUrl: 'https://github.com/demo/DEMO-ios/pull/109/files',
                    },
                    featureAffiliations: [],
                },
                associatedStabilityIssues: [],
                explanationText:
                    'This PR is open against the release, and it references this ticket in its title or branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'openPRWithTicket',
                fixRequest: {
                    id: '886214db270bb25e198281bf2d723c72',
                    createdBy: 'narmstrong@demo.com',
                    createdAt: '2022-03-03T17:53:35.403-06:00',
                    details: 'We need to include this change on the next release.',
                    status: {
                        status: 'Rejected',
                        updatedAt: '2022-03-04T20:53:35.403-06:00',
                        authorId: 'aearhart@demo.com',
                    },
                },
            },
            {
                id: '83541443f62cee376714c9bca54b1172',
                ticket: {
                    identifier: 'DEMO-10',
                    description: 'Migrate old user models',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-10',
                    owner: 'Amelia Earhart',
                    reporter: 'Amelia Earhart',
                    status: 'In Progress',
                    createdAt: '2021-01-12T14:56:47.903-06:00',
                    updatedAt: '2022-03-22T07:30:26.552-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                        { resolvedString: 'tech-debt', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: null,
                associatedStabilityIssues: [],
                explanationText:
                    'This ticket is associated with the release via a feature affiliation; no code in the release references this ticket',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'noCodeOnlyTicket',
            },
            {
                id: '7b55b2d37884932ee7a9ef4a63836a47',
                ticket: {
                    identifier: 'DEMO-20',
                    description: 'Add support for delete account',
                    fullDescription: null,
                    url: 'https://demo.atlassian.net/browse/DEMO-20',
                    owner: 'Sully Sullenberger',
                    reporter: 'Sully Sullenberger',
                    status: 'Selected for Development',
                    createdAt: '2022-02-09T10:05:10.674-06:00',
                    updatedAt: '2022-03-18T17:49:46.141-06:00',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        { resolvedString: 'important', integrationId: 'jira', affiliationType: 'jira-label' },
                    ],
                    additionalFields: {},
                },
                code: null,
                associatedStabilityIssues: [],
                explanationText:
                    'This ticket is associated with the release via a feature affiliation; no code in the release references this ticket',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                debugVariant: 'noCodeOnlyTicket',
            },
            {
                id: 'f6b2d4a2e8ab469fa2d57865bbad172d',
                ticket: {
                    identifier: 'DEMO-3451',
                    description: 'Add dark mode support',
                    fullDescription:
                        'Implement dark mode support across the entire app UI. Ensure all screens and components adapt to dark mode seamlessly. Design references: https://www.figma.com/file/XYZ/Dark-Mode-Design',
                    url: 'https://demo.atlassian.net/browse/DEMO-3451',
                    owner: 'Alice Johnson',
                    reporter: 'Bob Smith',
                    status: 'In Progress',
                    createdAt: '2024-05-01T10:15:30.000Z',
                    updatedAt: '2024-06-15T11:20:00.000Z',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'd89f123',
                    url: 'https://github.com/demo/DEMO-ios/commit/8300d65cbede86304ef9ba909ecb8e3e64bfa1cb',
                    author: 'alicejohnson',
                    message:
                        '[DEMO-3451] Add dark mode support (#1234)\n\n* Add dark mode toggle\n* Update UI components for dark mode\n* Fix styling issues in dark mode',
                    branch: 'release/1.2.0',
                    committedAt: '2024-06-14T14:00:00Z',
                    sourcePR: {
                        number: 1234,
                        url: 'https://github.com/demo/DEMO-ios/pull/1234',
                        featureAffiliations: [
                            {
                                resolvedString: '3.0.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: [],
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
            {
                id: 'ab5c3d4e6f8d4f07a9d765dcb9b123d4',
                ticket: {
                    identifier: 'DEMO-1237',
                    description: 'Optimize app startup time',
                    fullDescription:
                        'Reduce the startup time of the mobile app by optimizing resource loading and initialization processes. Analyze and address performance bottlenecks.',
                    url: 'https://demo.atlassian.net/browse/DEMO-1237',
                    owner: 'Emma Davis',
                    reporter: 'David Brown',
                    status: 'Done',
                    createdAt: '2024-04-15T08:00:00.000Z',
                    updatedAt: '2024-06-10T12:30:00.000Z',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: true,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'performance',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'e34f567',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'emmadavis-codes',
                    message:
                        '[DEMO-1237] Optimize startup time (#5678)\n\n* Improve resource loading\n* Reduce initialization overhead\n* Remove unnecessary delays',
                    branch: 'release/3.0.0',
                    committedAt: '2024-06-09T10:00:00Z',
                    sourcePR: {
                        number: 5678,
                        url: 'https://github.com/demo/DEMO-ios/pull/5678',
                        featureAffiliations: [
                            {
                                resolvedString: 'performance',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                            {
                                resolvedString: '3.0.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: true,
                isInLatestOrSelectedStagingBuild: true,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: [],
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
            {
                id: 'c7f8d9e0b1a2e3d4f5a6b7c8d9e0f1a2',
                ticket: {
                    identifier: 'DEMO-4568',
                    description: 'Implement Dark Mode',
                    fullDescription:
                        'Add a dark mode feature to the mobile app, allowing users to switch between light and dark themes. Ensure compatibility with existing functionality and design elements.',
                    url: 'https://demo.atlassian.net/browse/DEMO-4568',
                    owner: 'Olivia Smith',
                    reporter: 'John Doe',
                    status: 'In Progress',
                    createdAt: '2024-05-10T09:30:00.000Z',
                    updatedAt: '2024-06-15T14:45:00.000Z',
                    isTicketMissingFeatureAffiliation: false,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'dark-mode',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'd45e678',
                    url: 'https://github.com/demo/DEMO-ios/commit/abc1234567890def1234567890abcdef12345678',
                    author: 'oliviasmith-codes',
                    message:
                        '[DEMO-4568] Implement Dark Mode (#7890)\n\n* Add dark theme support\n* Update UI components for dark mode\n* Ensure theme toggling works smoothly',
                    branch: 'feature/dark-mode',
                    committedAt: '2024-06-14T11:30:00Z',
                    sourcePR: {
                        number: 7890,
                        url: 'https://github.com/demo/DEMO-ios/pull/7890',
                        featureAffiliations: [
                            {
                                resolvedString: 'dark-mode',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                            {
                                resolvedString: '3.1.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: [],
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff for the release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: true,
                associatedStabilityIssues: [],
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
            {
                id: 'd8764b8e4766195c12e7c3e418ab9432',
                ticket: {
                    identifier: 'DEMO-1924',
                    description: 'Enhance login security',
                    fullDescription: 'Implement two-factor authentication for enhanced security during user login.',
                    url: 'https://jira.example.com/browse/DEMO-1924',
                    owner: 'Maria Gonzalez',
                    reporter: 'Maria Gonzalez',
                    status: 'In Review',
                    createdAt: '2024-04-30T10:15:00.000+02:00',
                    updatedAt: '2024-06-14T09:56:21.877+02:00',
                    isTicketMissingFeatureAffiliation: true,
                    isTicketSubtask: false,
                    isTicketDone: false,
                    project: {
                        identifier: 'Demo',
                        displayableName: 'Demo',
                        url: 'https://demo.atlassian.net/browse/Demo',
                    },
                    featureAffiliations: [
                        {
                            resolvedString: 'security',
                            integrationId: 'jira',
                            affiliationType: 'jira-label',
                        },
                    ],
                    additionalFields: {},
                },
                code: {
                    status: 'merged',
                    type: 'commit',
                    identifier: 'fdbef35',
                    url: 'https://github.com/demo/DEMO-ios/commit/36d02a32b7539362c710128876a291ac7754c3a0',
                    author: 'mariagonzalez',
                    message:
                        '[DEMO-1924] Implement two-factor authentication (#2827)\n\n\r\nCo-authored-by: Igor Fedotov',
                    branch: 'develop',
                    committedAt: '2024-05-01T14:22:13Z',
                    sourcePR: {
                        number: 2827,
                        url: 'https://github.com/demo/DEMO-ios/pull/3248',
                        featureAffiliations: [
                            {
                                resolvedString: 'Needed for Release 👀',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                            {
                                resolvedString: '2.74.0',
                                integrationId: 'github',
                                affiliationType: 'github-milestone',
                            },
                        ],
                    },
                    sourceCommit: null,
                    manualCherryPickCommands: null,
                    manualCherryPickContext: null,
                    unsquashedChildCommitForPR: null,
                },
                explanationText:
                    'This code exists in the diff on the working branch since last release, and it references this ticket in its commit message, former PR title, or former branch name',
                ignored: false,
                autoIgnored: false,
                isItemDone: false,
                isInLatestOrSelectedStagingBuild: false,
                isInLatestWorkingBranchBuild: false,
                associatedStabilityIssues: null,
                debugVariant: 'codeMergedWithTicket',
                automationTooltipMessage: null,
                fixRequest: null,
            },
        ],
        percentComplete: 38,
        workingBranchPercentComplete: 56,
    };
    return featuresStep;
};

export const syncFeatureAffiliationsDataWithFeatureStepData: (
    resolvedFeatureAffiliation: ResolvedFeatureAffiliation,
    featuresStep: RecursiveStringifyEnums<ReleaseStepFeatureReadinessData>,
    release: RecursiveStringifyEnums<Release>,
    appPlatform: AppPlatform,
) => RecursiveStringifyEnums<ReleaseStepFeatureReadinessData> = (
    resolvedFeatureAffiliation,
    featuresStep,
    release,
    appPlatform,
) => {
    const updatedFeaturesStep = {
        ...featuresStep,
    };

    updatedFeaturesStep.items = updatedFeaturesStep?.items!.map((item) =>
        massageFeatureItem(
            item as FeatureItem,
            resolvedFeatureAffiliation.integrationId,
            resolvedFeatureAffiliation.affiliationType,
            release.version!,
            featuresStep.releaseBranch!,
            appPlatform,
        ),
    );

    updatedFeaturesStep.workingBranchItems = featuresStep?.workingBranchItems!.map((item) =>
        massageFeatureItem(
            item as FeatureItem,
            resolvedFeatureAffiliation.integrationId,
            resolvedFeatureAffiliation.affiliationType,
            release.version!,
            featuresStep.workingBranch!,
            appPlatform,
        ),
    );

    return updatedFeaturesStep;
};
